import type { ReactNode } from 'react'
import { PartThumbnail } from '../../components/PartThumbnail'
import { FileTextOutlined, InfoCircleOutlined, PictureOutlined } from '../../components/icons'
import { Chip } from '../../components/ui/Chip'
import { EmptyState } from '../../components/ui/EmptyState'
import { useToast } from '../../components/ui/ToastProvider'
import type { OrderDetailData, OrderDocument, OrderLineWithThumbnail, Shipment } from './domain'
import {
  SHIPMENT_STATUS_UI,
  deriveShipmentStatusLines,
  estimateShipDateFromDelivery,
  formatDate,
  formatShipmentShippingMethod,
  isSafeDocumentReference,
  resolveContractEstShipDate,
  resolveLineDeliveryDate,
  resolveLineLabel,
  resolveShipmentHeadlineDate,
} from './domain'
import { StatusIcon } from './statusIcon'

export interface ShipmentsPanelProps {
  readonly order: OrderDetailData
}

/**
 * Shipments tab panel (UX spec §2.2 item 4, §2.7 / UID-123 S8 item 4;
 * UID-133 design-fidelity pass). Restructured to match the design's actual
 * structure (`Fictiv Platform.dc.html` ~L3708-3740, "Shipment 1"): ONE CARD
 * PER SHIPMENT — a "Shipment N" heading + status pill, then the prototype's
 * 4-field detail grid (status headline date + tracking · Ship to · Shipping
 * method · Shipping status lines), then a bordered per-part table with a
 * thumbnail placeholder, filename + drawing chip, description, qty, and a
 * "Quality documents and photos" doc button, sourced from `order.documents`
 * filtered to that shipment's lines.
 *
 * UID-133 restyle (visual/layout only — no data-wiring or contract change):
 * the previous 8-field `dl` (Ship to / method / est-ship / carrier /
 * tracking / shipped / delivered / signed-by) is replaced by the prototype's
 * 4-column grid. The dropped fields are folded into the "Shipping status"
 * cell's derived lines ({@link deriveShipmentStatusLines}) rather than lost:
 * carrier → "On {carrier} network", signedBy → "Signed by {name}", the dates
 * → the status/est-ship lines. The parts table gains the prototype's canvas
 * header with an info icon, an inset-hairline thumbnail tile, a bordered
 * drawing chip, and a button-styled quality-docs link — matching the sibling
 * Customs tab (already redesigned under UID-130 / PR #140).
 *
 * This supersedes the previous layout, which rendered a page-level
 * "Per-line tracking" table (`PerLineTrackingTable`, not present in the
 * design at all) plus disconnected shipment `dl` cards with no
 * thumbnails/doc links, and a separate page-level `DocumentsSection` for
 * every document regardless of which shipment (if any) covers it. All three
 * concerns now live inside this one per-shipment card, matching the design.
 *
 * DATA MAPPING (verified NOT a contract gap): `Shipment.lines` gives
 * `{ orderLineId }` per member (`ShipmentLine`); `OrderDocument.orderLineId`
 * lets per-part `annotated_pdf` (drawing) / `quality_doc` documents be
 * filtered to exactly the lines a shipment bundles. Both `order.shipments`
 * and `order.documents` are real, DETAIL-only projections.
 *
 * UID-224 descope: the lifecycle/reconciler/customs/polling work originally
 * scoped for this ticket was cut. The backend now guarantees every placed
 * order carries exactly one `pending` "Shipment 1", so the per-line status
 * FALLBACK this panel used to render while `order.shipments` was empty
 * (`LineStatusFallbackList`) is dead — `order.shipments` is never empty for
 * a placed order anymore. The only remaining empty state is the genuine
 * "nothing has shipped yet" case (an order with no lines at all).
 *
 * UID-411: the design's per-part row shows a real product thumbnail image
 * (`p.img`) — `OrderDetailData['lines'][number].thumbnailUrl` (real, Library-
 * resolved, added on top of the submodule `OrderLine` via a local `allOf`
 * wrapper, ADR-0126 extension) now renders a real `<img>` there, falling back
 * to the existing decorative placeholder swatch on a miss or load failure.
 * The design's "Description" column remains a field distinct from the part
 * name/filename that doesn't exist on `OrderLine` — this reuses the existing
 * `partNumber` (already surfaced as a secondary caption elsewhere on this
 * screen) rather than fabricating new copy. When a line has a `partNumber`
 * but no `displayName`, the resolved Name cell IS that same `partNumber`
 * (`resolveLineLabel`'s fallback order) — the Description cell is left
 * blank (an em dash) rather than showing the identical string twice
 * (Gate-5 review fix).
 */
export function ShipmentsPanel({ order }: ShipmentsPanelProps) {
  const documents = order.documents ?? []
  // UID-212 follow-up: the Shipments tab ALWAYS shows at least one shipment for a placed order,
  // regardless of status. A live order gets one at placement (buildInitialShipment); when an order
  // carries no shipment row (a legacy/seed order with none, or a fail-open placement write that
  // didn't land), synthesize a single "Preparing" (pending) shipment covering all its lines — the
  // same shape buildInitialShipment produces — so the tab is never a bare empty state for an order
  // that has lines. Only a truly empty order (no shipments AND no lines) shows the empty state.
  const shipments =
    order.shipments && order.shipments.length > 0
      ? order.shipments
      : order.lines.length > 0
        ? [synthesizePreparingShipment(order)]
        : []

  if (shipments.length === 0) {
    return (
      <EmptyState
        title="Nothing has shipped yet"
        detail="You'll see shipment details here once your order ships."
      />
    )
  }

  return (
    <ul className="shipment-list">
      {shipments.map((shipment) => (
        <ShipmentCard key={shipment.id} shipment={shipment} order={order} documents={documents} />
      ))}
    </ul>
  )
}

/**
 * A client-side "Preparing" shipment for an order that carries NO shipment row (a legacy/seed
 * order with none, or a fail-open placement write that didn't land) — mirrors the shared
 * `buildInitialShipment`: one `pending` shipment (seq 1) covering every line, all lifecycle stamps
 * null. Display-only fallback so the Shipments tab is never a bare empty state for an order with
 * lines; a real persisted shipment always takes precedence.
 */
function synthesizePreparingShipment(order: OrderDetailData): Shipment {
  return {
    id: order.id,
    sequenceIndex: 1,
    status: 'pending',
    carrier: null,
    serviceLevel: null,
    trackingNumber: null,
    estShipDate: null,
    shippedAt: null,
    deliveredAt: null,
    signedBy: null,
    lines: order.lines.map((line) => ({ orderLineId: line.id })),
  }
}

/**
 * UID-317 review follow-up: render the shipping method on TWO lines — the method type over its
 * detail (e.g. "Own account" / "UPS ••0001") — mirroring the payment-method treatment, so a long
 * value doesn't wrap mid-token in its grid column. The " · " separator is dropped (it only exists
 * to join the two parts on one line); a single-token method (e.g. "Ground") stays on one line.
 */
function renderShippingMethod(method: string | null): ReactNode {
  if (!method) {
    return <span className="caption-text">—</span>
  }
  return method.split(' · ').map((part, index) => (
    <span key={part}>
      {index > 0 && <br />}
      {part}
    </span>
  ))
}

function ShipmentCard({
  shipment,
  order,
  documents,
}: {
  readonly shipment: Shipment
  readonly order: OrderDetailData
  readonly documents: readonly OrderDocument[]
}) {
  const address = order.shippingAddress
  const statusMeta = SHIPMENT_STATUS_UI[shipment.status]
  const shipmentLineIds = new Set(shipment.lines.map((line) => line.orderLineId))
  const lines = order.lines.filter((line) => shipmentLineIds.has(line.id))
  // UID-317 review follow-up: the system stores only a DELIVERY date (prepay `estDeliveryDate` /
  // the line's committed date); there is no distinct est-SHIP date anywhere. For a `pending`
  // ("Preparing") shipment we therefore show the DELIVERY date in the headline, and derive a
  // distinct "Est. ship" for the status line as `delivery − transit` (estimateShipDateFromDelivery
  // — a documented STUB) so the two surfaces don't just duplicate the same date. Non-pending
  // shipments keep the real stamps (delivered → deliveredAt, shipped → shippedAt/estShipDate).
  const committedDeliveryDate =
    lines
      .map((line) => resolveLineDeliveryDate(line))
      .filter((date): date is string => date !== null)
      .sort()
      .at(-1) ?? null
  const isPreparing = shipment.status === 'pending'
  // The best available DELIVERY date for a preparing shipment: the shipment's stored date (which is
  // the delivery-flavored estDeliveryDate at placement) → the prepay contract date → the line's
  // committed delivery date.
  const deliveryDate = isPreparing
    ? (shipment.estShipDate ?? resolveContractEstShipDate(order) ?? committedDeliveryDate)
    : null
  const serviceLevel =
    shipment.serviceLevel ??
    (order.shippingMethod?.kind === 'prepay' ? (order.shippingMethod.serviceLevel ?? null) : null)
  const estShipDate = isPreparing
    ? estimateShipDateFromDelivery(deliveryDate, serviceLevel)
    : shipment.estShipDate
  const effectiveShipment: Shipment =
    estShipDate === shipment.estShipDate
      ? shipment
      : // `as Shipment`: under exactOptionalPropertyTypes, spreading a Shipment (whose optional
        // carrier/serviceLevel/… props TS widens to `T | undefined`) isn't structurally assignable
        // back to Shipment. The runtime object IS a valid Shipment — a real one plus an overridden
        // estShipDate (string | null) — so the cast is sound.
        ({ ...shipment, estShipDate } as Shipment)
  const statusLines = deriveShipmentStatusLines(effectiveShipment)
  // "Preparing" headline shows the DELIVERY date; other statuses keep resolveShipmentHeadlineDate.
  const headlineDate =
    isPreparing && deliveryDate ? deliveryDate : resolveShipmentHeadlineDate(effectiveShipment)
  const showTracking =
    !!shipment.trackingNumber &&
    (shipment.status === 'shipped' ||
      shipment.status === 'in_transit' ||
      shipment.status === 'delivered')

  // Plain per-shipment ordinal ("Shipment 1", "Shipment 2") — no "of M"
  // suffix, matching the cart's ship-as-ready breakdown labels.
  const heading = `Shipment ${shipment.sequenceIndex}`

  return (
    <li className="shipment-card">
      <div className="shipment-card-header">
        <h3 className="shipment-card-heading">{heading}</h3>
        {/* UID-212 follow-up: the design's "Preparing" (pending) pill carries NO icon — the
        circular clock is dropped for this status (the pill's neutral tone + self-describing text
        need no icon). Other statuses keep their icon (delivered/in-transit still pair color+icon). */}
        <Chip
          tone={statusMeta.tone}
          icon={
            shipment.status === 'pending' ? undefined : <StatusIcon iconKey={statusMeta.iconKey} />
          }
        >
          {statusMeta.label}
        </Chip>
      </div>

      {/* Prototype's 4-field detail grid (`Fictiv Platform.dc.html` ~L3712). */}
      <div className="shipment-card-info-grid">
        <div className="shipment-info-field">
          <span className="shipment-info-label">{statusMeta.label}</span>
          <span className="shipment-info-headline">{formatDate(headlineDate)}</span>
          {showTracking && (
            <>
              <span className="shipment-info-label shipment-info-label-tracking">
                Tracking number
              </span>
              <span className="shipment-tracking-number">{shipment.trackingNumber}</span>
            </>
          )}
        </div>
        <div className="shipment-info-field">
          <span className="shipment-info-label">Ship to</span>
          <div className="shipment-ship-to">
            {address ? (
              <>
                <span>{address.line1}</span>
                {address.line2 && <span>{address.line2}</span>}
                <span>
                  {address.city}
                  {address.region ? `, ${address.region}` : ''} {address.postalCode}
                </span>
              </>
            ) : (
              <span className="caption-text">—</span>
            )}
          </div>
        </div>
        <div className="shipment-info-field">
          <span className="shipment-info-label">Shipping method</span>
          <span className="shipment-info-value">
            {renderShippingMethod(formatShipmentShippingMethod(order, shipment))}
          </span>
        </div>
        <div className="shipment-info-field">
          <span className="shipment-info-label">Shipping status</span>
          <div className="shipment-status-lines">
            {statusLines.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </div>
        </div>
      </div>

      <ShipmentPartsTable lines={lines} documents={documents} />
    </li>
  )
}

function ShipmentPartsTable({
  lines,
  documents,
}: {
  readonly lines: readonly OrderLineWithThumbnail[]
  readonly documents: readonly OrderDocument[]
}) {
  if (lines.length === 0) {
    return <p className="caption-text">No parts are recorded for this shipment.</p>
  }

  return (
    <div className="shipment-parts-table-container">
      <div className="data-table-wrapper">
        <table className="data-table shipment-parts-table">
          <caption className="sr-only">Parts in this shipment</caption>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Description</th>
              <th scope="col">Qty</th>
              <th scope="col">
                Quality documents and photos
                <InfoCircleOutlined aria-hidden="true" className="customs-column-info-icon" />
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <ShipmentPartRow key={line.id} line={line} documents={documents} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ShipmentPartRow({
  line,
  documents,
}: {
  readonly line: OrderLineWithThumbnail
  readonly documents: readonly OrderDocument[]
}) {
  const label = resolveLineLabel(line)
  const lineDocs = documents.filter((doc) => doc.orderLineId === line.id)
  const drawing = lineDocs.find((doc) => doc.kind === 'annotated_pdf')
  // The "Quality documents and photos" affordance is offered on EVERY part row
  // (the design shows quality docs per part). When a line has a seeded
  // `quality_doc`, link that; otherwise fall back to a demo-referenced
  // placeholder so the button still renders (its `demo://` reference is not a
  // safe/navigable target, so DocumentLink renders the same dummy toast button
  // the rest of the screen's demo actions use).
  const qualityDoc: OrderDocument = lineDocs.find((doc) => doc.kind === 'quality_doc') ?? {
    id: `quality-doc-${line.id}`,
    kind: 'quality_doc',
    label: 'Quality documents and photos',
    reference: 'demo://quality-docs',
    orderLineId: line.id,
  }
  // Gate-5 review fix: when a line has a `partNumber` but no `displayName`,
  // `resolveLineLabel` falls back to `partNumber` — so the Name and
  // Description cells would otherwise show the identical string. Suppress
  // the Description cell in that case rather than render a visible
  // duplicate.
  const description = line.partNumber && line.partNumber !== label ? line.partNumber : null

  return (
    <tr>
      <td>
        <div className="shipment-part-name-cell">
          <PartThumbnail
            url={line.thumbnailUrl}
            imgClassName="shipment-part-thumbnail-img"
            placeholder={<span className="shipment-part-thumbnail" aria-hidden="true" />}
          />
          <div className="shipment-part-name-text">
            <span className="line-item-name">{label}</span>
            {drawing && (
              <DocumentLink
                doc={drawing}
                label={drawing.label}
                className="shipment-part-drawing-chip"
                icon={<FileTextOutlined aria-hidden="true" />}
              />
            )}
          </div>
        </div>
      </td>
      <td>{description ?? <span className="caption-text">—</span>}</td>
      <td>{line.quantity.value}</td>
      <td>
        <DocumentLink
          doc={qualityDoc}
          label="View quality documents and photos"
          className="shipment-quality-doc-link"
          icon={<PictureOutlined aria-hidden="true" />}
          toastMessage="Opening quality documents and photos"
        />
      </td>
    </tr>
  )
}

/** Shared safe-reference-checked document link (same {@link isSafeDocumentReference}
 * ActionBar uses) — one home so a per-shipment doc link can't drift from the
 * rest of the screen's link-hardening behavior. A real (safe) reference renders a
 * navigating `<a>`. A demo `demo://…` reference (not navigable) renders an interactive
 * BUTTON that toasts on click — matching the screen's other demo actions — instead of an
 * inert label. The optional `icon` is rendered aria-hidden ahead of the label. */
function DocumentLink({
  doc,
  label,
  className,
  icon,
  toastMessage = 'Downloading documents…',
}: {
  readonly doc: OrderDocument
  readonly label: string
  readonly className?: string
  readonly icon?: ReactNode
  /** Message shown when a demo (non-navigable) reference is clicked; matches the
   * action's own wording (e.g. the quality-docs button reads "View …" → "Opening …"). */
  readonly toastMessage?: string
}) {
  const { showToast } = useToast()
  if (!isSafeDocumentReference(doc.reference)) {
    return (
      <button
        type="button"
        className={className}
        onClick={() => showToast(toastMessage, 'success')}
      >
        {icon}
        <span>{label}</span>
      </button>
    )
  }
  const isExternal = doc.reference.startsWith('http://') || doc.reference.startsWith('https://')
  return (
    <a
      href={doc.reference}
      className={className}
      {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {icon}
      <span>{label}</span>
    </a>
  )
}
